import type { PresenceUser } from '@colplat/protocol';
import { makeDraggable, type DraggableHandle } from '@colplat/ui-web';
import type { WebledgerClient } from '../webledgerClient.js';
import { apiFetch, failureDetail } from '../api.js';
import { ageLabel, knownOffline } from '../offlineReads.js';
import {
  balanceOf, foldSnapshot, restorePlan, timelineHtml,
  type CachedHistory, type CurrentSplit, type HistoryContext, type Snapshot,
  type TransactionHistory,
} from './webledgerHistory.js';

/**
 * The change-history panel, as a controller two different forms can mount.
 *
 * It started inside the transaction editor, which was fine while the editor
 * was the only way to reach a transaction's history. It is not any more: the
 * short form on the wallets page opens transactions in its own right, and
 * being thrown into the full editor to answer "what did this look like last
 * week" is a jarring answer to a small question — you lose the form you were
 * working in to read three lines.
 *
 * What is NOT duplicated by living here is the part that costs: deciding
 * whether to ask the server or trust the cache, refusing to restore without a
 * lease, refusing to restore from a snapshot whose splits have since been
 * removed, and never building a compare-and-swap out of a past version's
 * split versions. Every one of those is a way to corrupt a ledger quietly,
 * and a second implementation of them would be a second set of them.
 *
 * The host supplies what differs: how to paint a past version into its own
 * fields, and what it currently holds. Everything else is here.
 */
export interface HistoryPanelHost {
  /** The transaction being examined, and the token to ask about it with. */
  txId: string;
  token: string;
  client: WebledgerClient;
  /** Where the panel is rendered. Emptied to close; nothing else writes into it. */
  mount: HTMLElement;
  /** The button that opened it, so `aria-expanded` tracks reality. Optional: a host may open it another way. */
  toggleBtn?: HTMLElement | undefined;
  /** Link target for "Open full page", already resolved against the mount path (invariant #10). */
  fullPageHref: string;

  /** Names for the ids history stores — rebuilt per paint, because the commits it numbers over change. */
  context: (history: TransactionHistory | undefined) => HistoryContext;
  /** The version a restore will compare-and-swap against. */
  baseVersion: () => number;
  /** The lease this form holds, if any. Restoring is an ordinary edit and needs one. */
  leaseId: () => string | undefined;
  /** False when the host's form is not editable at all — a viewer's session, say. */
  canEdit: () => boolean;
  /** The LIVE splits and their versions. Never a snapshot's: see `paint`. */
  currentSplits: () => CurrentSplit[];

  /**
   * Paint a past version into the host's own fields, read-only.
   *
   * Returns false when the host's form cannot represent it — the short form
   * has two legs and a snapshot may have five. Saying so is better than
   * painting two of the five, which would be a quiet lie about what that
   * version was.
   *
   * A host that returns false has to tell the reader something itself. The
   * panel does not, because only the host knows what it did instead: one of
   * them hands over to a form that CAN show it, and "open the full editor"
   * is poor advice to somebody already on their way there.
   */
  showAsOf: (snapshot: Snapshot, commitId: string) => boolean | Promise<boolean>;
  /** Put the form back to the live record. Called on close and after a restore. */
  backToCurrent: () => void | Promise<void>;

  /**
   * The host's own message areas, so the panel does not invent a second style
   * of each. `notice` returns the element it wrote into: the way back from a
   * past version belongs to the panel, not to the host, and it has to be
   * wired to something.
   */
  notice: (html: string | null) => HTMLElement | null;
  showError: (message: string) => void;
  notify: (message: string) => void;
  /** After a restore landed, so the host can refresh whatever list it belongs to. */
  onRestored: () => void;

  escapeHtml: (s: string) => string;
  presenceDotHtml: (user: PresenceUser, size?: number) => string;
  timeAgo: (iso: string) => string;
}

export interface HistoryPanel {
  /** Open, or close again if it is already open — this is a toggle, as the button it hangs off implies. */
  toggle: () => Promise<void>;
  open: () => Promise<void>;
  /** `restoreForm: false` for a teardown, where there is no form left to put back. */
  close: (restoreForm?: boolean) => void;
  isOpen: () => boolean;
  /** The commit the host's form is currently showing, if it is showing a past one. */
  viewing: () => string | undefined;
  /** Repaint in place — the host calls this when the lease changes, since that changes what can be restored. */
  repaint: () => void;
  /** Forget the loaded history, for a host reused across transactions. */
  reset: () => void;
}

export function createHistoryPanel(host: HistoryPanelHost): HistoryPanel {
  let historyData: TransactionHistory | undefined;
  /** Set when what is on screen came from the cache rather than the server. */
  let staleSince: string | undefined;
  let viewingCommitId: string | undefined;
  let drag: DraggableHandle | undefined;
  /**
   * The LIVE splits and version, kept from the moment before a past version
   * was painted into the host's fields.
   *
   * This is what makes "Restore this version" offerable while you are
   * looking at one. The rule it exists to keep is unchanged and absolute: a
   * restore is an ordinary edit and must compare-and-swap against the
   * record as it stands now (invariant #2), never against the versions
   * carried by a snapshot. The host cannot answer that question while it is
   * showing a snapshot — its fields hold one — so the answer is taken
   * before the question becomes unanswerable, rather than the button being
   * taken away.
   *
   * Which it was, and it made the panel read as though looking at a version
   * disqualified it from being restored: you pressed "View as of here" to
   * check you had the right one, and the way to act on it disappeared.
   *
   * If somebody else changes the transaction while it is being read, these
   * go stale and the restore comes back a version conflict — which is the
   * correct outcome, said out loud, instead of a silent overwrite.
   */
  let liveBeforeViewing: { splits: CurrentSplit[]; baseVersion: number } | undefined;

  /** The live record's splits — never the snapshot's, see `liveBeforeViewing`. */
  const liveSplits = (): CurrentSplit[] => liveBeforeViewing?.splits ?? host.currentSplits();
  const liveVersion = (): number => liveBeforeViewing?.baseVersion ?? host.baseVersion();

  const esc = host.escapeHtml;

  function isOpen(): boolean { return host.mount.innerHTML !== ''; }

  function close(restoreForm = true): void {
    drag?.dispose();
    drag = undefined;
    host.mount.innerHTML = '';
    host.toggleBtn?.setAttribute('aria-expanded', 'false');
    // Closing while a past version is on screen would otherwise leave the
    // form showing values that are not the record's, with nothing left on
    // screen to say so.
    if (restoreForm && viewingCommitId !== undefined) void backToCurrent();
  }

  /**
   * The frame is built once per opening and only its body replaced after.
   * Rebuilding the whole panel on every repaint would throw away where the
   * user dragged it — and it repaints whenever the lease changes.
   */
  function render(bodyHtml: string): void {
    const body = host.mount.querySelector<HTMLElement>('.cf-timeline__body');
    if (body) { body.innerHTML = bodyHtml; wireActions(); return; }

    host.mount.innerHTML = `
      <aside class="cf-timeline" role="dialog" aria-label="Change history">
        <div class="cf-timeline__head" data-tl-handle>
          <span class="cf-drag-grip" aria-hidden="true">⠿</span>
          <strong class="flex-grow-1" style="font-size:.85rem">Change history</strong>
          <a class="btn btn-sm btn-link" href="${host.fullPageHref}">Open full page</a>
          <button type="button" class="btn-close" data-tl-close aria-label="Close history"></button>
        </div>
        <div class="cf-timeline__body">${bodyHtml}</div>
      </aside>`;
    host.toggleBtn?.setAttribute('aria-expanded', 'true');
    // Wrapped, not passed directly: the click event would arrive as
    // `restoreForm` and be truthy by accident rather than by intent.
    host.mount.querySelector('[data-tl-close]')?.addEventListener('click', () => close());

    // Movable, because this panel sits over the amounts you most want to
    // compare a past version against.
    const panel = host.mount.querySelector<HTMLElement>('.cf-timeline');
    const handle = host.mount.querySelector<HTMLElement>('[data-tl-handle]');
    drag?.dispose();
    drag = panel && handle ? makeDraggable({ panel, handle }) : undefined;

    wireActions();
  }

  function wireActions(): void {
    host.mount.querySelectorAll<HTMLButtonElement>('[data-tl-view]').forEach((btn) => {
      btn.addEventListener('click', () => { void viewAsOf(btn.dataset['tlView']!); });
    });
    host.mount.querySelectorAll<HTMLButtonElement>('[data-tl-restore]').forEach((btn) => {
      btn.addEventListener('click', () => { void restore(btn.dataset['tlRestore']!); });
    });
  }

  function repaint(): void {
    if (!historyData) return;
    const staleNote = staleSince
      ? `<div class="alert alert-warning py-2 px-3 mb-2 small" role="status">Offline — this is the history `
        + `as saved on this device ${esc(ageLabel(staleSince))}. Anything changed since is not here.</div>`
      : '';
    render(staleNote + timelineHtml({
      history: historyData,
      ctx: host.context(historyData),
      // Restoring is an ordinary edit, so it needs what an edit needs: a
      // form that is actually editable, and therefore a lease. It is offered
      // while a past version is on screen too — `liveSplits` is what keeps
      // that safe.
      canRestore: host.canEdit() && host.leaseId() !== undefined,
      // exactOptionalPropertyTypes: omit rather than pass undefined.
      ...(viewingCommitId !== undefined ? { viewingCommitId } : {}),
      currentSplits: liveSplits(),
      escapeHtml: host.escapeHtml,
      presenceDotHtml: host.presenceDotHtml,
      timeAgo: host.timeAgo,
    }));
  }

  async function open(): Promise<void> {
    render('<p class="text-secondary small mb-0">Loading…</p>');
    const cache = host.client.docCache;
    const cached = cache.get<CachedHistory>(`history:${host.txId}`);

    /*
     * The replica first, and BEFORE the offline branch below (ADR-0025).
     *
     * This read used to sit in the online path, under a branch that returned
     * early whenever there was no connection — so offline the one store that
     * actually holds the histories was never consulted, and the panel fell
     * back to `docCache`, which only ever holds the handful of transactions
     * whose history somebody had already opened online. The symptom was
     * exactly that: one entry showed its history, the next said it had none.
     *
     * Order matters and it is the ADR's own rule: the same path runs whether
     * or not there is a connection. A copy here is current by construction —
     * `HistorySync` invalidates one the moment anything in its document group
     * changes — so there is never a reason to prefer the network to it.
     */
    const local = host.client.dataset.history<TransactionHistory>(host.txId);
    if (local) {
      historyData = local;
      staleSince = undefined;
      repaint();
      return;
    }

    // Offline: whatever we hold, or an honest nothing. Never a blank panel.
    if (knownOffline() || host.client.transport.state !== 'open') {
      if (!cached) {
        render('<p class="text-secondary small mb-0">You are offline, and this transaction’s '
          + 'history has not reached this device yet.</p>');
        return;
      }
      historyData = cached.value;
      staleSince = cached.cachedAt;
      repaint();
      return;
    }

    /*
     * Online: always ask.
     *
     * This used to serve the cache whenever `atVersion` matched the
     * transaction's current `version`, which is a freshness test that does
     * not hold. A transaction's history covers its child records too, and a
     * child can change without the parent's version moving — tagging is
     * exactly that, and the symptom was a timeline that was right about
     * everything except the thing you had just done.
     *
     * Rather than find a version number that covers the children, drop the
     * shortcut. Opening the history is a deliberate and infrequent act, and
     * one request is the right price for it. The cache's job is the one
     * ADR-0011 gives it: answering when there is nobody to ask.
     */

    const res = await apiFetch(`/apps/webledger/transactions/${host.txId}/history`, { token: host.token })
      .catch(() => undefined);
    if (!res?.ok) {
      // The network went between opening and asking. Fall back rather than
      // showing an error over data we already have.
      if (cached) {
        historyData = cached.value;
        staleSince = cached.cachedAt;
        repaint();
        return;
      }
      render('<p class="text-secondary small mb-0">This entry\u2019s history has not reached this device yet, '
        + 'and there is no connection.</p>');
      return;
    }
    const fresh = (await res.json()) as TransactionHistory;
    await cache.set(`history:${host.txId}`, { ...fresh, atVersion: host.baseVersion() });
    // Straight into the replica too, so the next open needs no request at all.
    void host.client.dataset.putHistory(host.txId, fresh);
    historyData = fresh;
    staleSince = undefined;
    repaint();
  }

  async function toggle(): Promise<void> {
    if (isOpen()) { close(); return; }
    await open();
  }

  /** Show the host's form as it stood after a commit, without writing anything. */
  async function viewAsOf(commitId: string): Promise<void> {
    if (!historyData) return;
    const snapshot = foldSnapshot(historyData.commits, commitId);

    // Set before painting, not after: the host's paint may consult it, and
    // more to the point a host that cannot show this version must still not
    // be left looking editable while the panel thinks it is viewing.
    viewingCommitId = commitId;
    // Before the paint, because the paint is what overwrites them. Kept
    // across one view to the next: stepping straight from one past version
    // to another never passes back through the live record, so a second
    // capture would capture the first snapshot.
    liveBeforeViewing ??= { splits: host.currentSplits(), baseVersion: host.baseVersion() };
    const shown = await host.showAsOf(snapshot, commitId);
    if (!shown) {
      viewingCommitId = undefined;
      liveBeforeViewing = undefined;
      repaint();
      return;
    }
    repaint(); // so the entry being viewed is marked as such
    const where = host.notice(`Showing this transaction as it stood after the selected change`
      + ` (splits summed to ${esc(balanceOf(snapshot))}).`
      + ` <button type="button" class="btn btn-sm btn-outline-secondary ms-2" data-tl-back>Back to current</button>`);
    where?.querySelector('[data-tl-back]')?.addEventListener('click', () => { void backToCurrent(); });
  }

  async function backToCurrent(): Promise<void> {
    viewingCommitId = undefined;
    liveBeforeViewing = undefined;
    await host.backToCurrent();
    if (isOpen()) repaint();
  }

  /**
   * Restore = a normal edit that happens to carry old values. No new server
   * capability, no rewriting of history: it appends a commit, and the restore
   * is itself visible in the timeline afterwards (invariant #7).
   */
  async function restore(commitId: string): Promise<void> {
    if (!historyData) return;
    const leaseId = host.leaseId();
    if (leaseId === undefined) {
      host.showError('Restoring needs this transaction reserved for you — it is currently read-only.');
      return;
    }
    const snapshot = foldSnapshot(historyData.commits, commitId);
    const plan = restorePlan(snapshot, host.txId, liveSplits(), historyData.splits ?? []);
    if (!plan.ok) {
      host.showError('That version cannot be restored: one of its splits has since been removed.');
      return;
    }
    const body = { ...plan.payload, baseVersion: liveVersion(), leaseId };

    const res = await apiFetch(`/apps/webledger/transactions/${host.txId}`, { method: 'PUT', token: host.token, body })
      .catch(() => undefined);
    if (!res?.ok) {
      host.showError(await failureDetail(res, 'Could not restore that version'));
      return;
    }
    // The server reports the version it ended on, and a write that changed
    // nothing leaves it where it was. Saying "restored" then would be a plain
    // untruth — and this is easy to hit, because restoring a version whose
    // values already match the current ones is exactly a no-op.
    const base = liveVersion();
    const { version } = (await res.json().catch(() => ({ version: base }))) as { version: number };
    if (version === base) {
      host.notify('That version is identical to the current one — nothing to restore.');
      close();
      return;
    }
    host.notify('That version has been restored, and the restore is recorded in the history.');
    historyData = undefined;
    viewingCommitId = undefined;  // `backToCurrent` below does the repopulating
    liveBeforeViewing = undefined;
    close(false);
    await backToCurrent();
    host.onRestored();
  }

  return {
    toggle, open, close, isOpen, repaint,
    viewing: () => viewingCommitId,
    reset: () => {
      historyData = undefined;
      staleSince = undefined;
      viewingCommitId = undefined;
      liveBeforeViewing = undefined;
    },
  };
}
